import * as mobx from 'mobx';
import { IntlShape } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { SHORT_PLAIN_TEXT_LENGTH } from '../../../constants/common.constants';
import { IRequestTelephoneNumberSet, IUpdateNumberSetDTO, IZoomDAO, IZoomTelephoneNumberSetDTO } from '../../../api/zoom.dao';
import { SHORT_PLAIN_TEXT_TOO_LONG_ERROR_MESSAGE } from '../../../constants/validation-errors';
import { IReactIntlProvider } from '../../../infrastructure/react-intl-provider';
import { AudioBridgeProvider } from '../../../api/connections.dao';
import { TELEPHONE_ARCHIVE_ERROR_TEXT } from '../../../constants/telephone-number-set-form.text';

class TelephoneFormDataVM {
    public defaultValues: IRequestTelephoneNumberSet = {
        name: '',
        provider: AudioBridgeProvider.ZOOM,
        oneTapFormula: '',
        telephoneArchive: null,
    };

    @mobx.observable
    public name: string = this.defaultValues.name;

    @mobx.observable
    public provider: string = this.defaultValues.provider;

    @mobx.observable
    public oneTapFormula: string = this.defaultValues.oneTapFormula;

    @mobx.observable
    public label: string;

    @mobx.observable
    public isFormChecked: boolean;

    @mobx.observable
    public serverErrorMessage: string;

    @mobx.observable
    public telephoneNumberSets: IZoomTelephoneNumberSetDTO[];

    @mobx.observable
    public telephoneArchive: File;

    @mobx.observable
    public audioProviderId: string;

    @mobx.observable
    public isEditEnabled: boolean;

    @mobx.observable
    public telephoneArchiveName: string;

    constructor(
        @IoC.Inject private readonly numberSetsDAO: IZoomDAO = IoC.Container.get(IZoomDAO),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        ) {
            this.telephoneNumberSets = [];
        }

    public get intl(): IntlShape { return this.intlProvider.getIntl(); }
    @mobx.action
    public setName = (name: string): void => {
        this.name = name;
    };

    @mobx.action
    public setProvider = (provider: string): void => {
        this.provider = provider;
    };

    @mobx.action
    public setOneTapFormula = (oneTapFormula: string): void => {
        this.oneTapFormula = oneTapFormula;
    };

    @mobx.action
    public setTelephoneArchive = (file: File) => {
        this.telephoneArchive = file;
    };

    @mobx.action
    public setIsEdit = (edit: boolean): void => {
        this.isEditEnabled = edit;
    };

    @mobx.action
    public initialize = (numberSetDetails?: IUpdateNumberSetDTO): void => {
        if (numberSetDetails) {
            this.name = numberSetDetails.name || this.defaultValues.name;
            this.provider = numberSetDetails.provider;
            this.oneTapFormula = numberSetDetails.oneTapFormula || this.defaultValues.oneTapFormula;
            this.audioProviderId = numberSetDetails.id;
        }
    };

    public getNameError(): string | null {
        if (!this.name) {
            return this.intl.formatMessage({
                id: `oe.telephone.telephoneNameIsRequired`,
                defaultMessage: 'Name is required',
                description: 'Error message in telephone number set popover'
            });
        } else if (this.name?.length > SHORT_PLAIN_TEXT_LENGTH) {
            return SHORT_PLAIN_TEXT_TOO_LONG_ERROR_MESSAGE(this.intl);
        }

        return null;
    }

    public getProviderError(): string | null {
        if (this.provider?.length > SHORT_PLAIN_TEXT_LENGTH) {
            return SHORT_PLAIN_TEXT_TOO_LONG_ERROR_MESSAGE(this.intl);
        }

        return null;
    }

    public getTelephoneArchiveError(): string | null {
        if (!this.telephoneArchive && !this.isEditEnabled) {
            return this.intl.formatMessage(TELEPHONE_ARCHIVE_ERROR_TEXT);
        }
        return null;
    }

    public isFormValid(): boolean {
        return !this.getNameError() && !this.getProviderError() && !this.getTelephoneArchiveError();
    }

    @mobx.action
    public allowToShowValidationErrors(): void {
        this.isFormChecked = true;
    }

    public showValidationErrors(): boolean {
        return this.isFormChecked;
    }

    public toApiCreateNumberSetDTO(): IRequestTelephoneNumberSet {
        return {
            name: this.name.trim() || null,
            provider: this.provider.trim(),
            oneTapFormula: this.oneTapFormula.trim(),
            telephoneArchive: this.telephoneArchive,
        };
    }

    public toApiUpdateNumberSetDTO(telephoneArchive?: File): IUpdateNumberSetDTO {
        return {
            id: this.audioProviderId,
            name: this.name.trim() || null,
            provider: this.provider.trim(),
            oneTapFormula: this.oneTapFormula.trim(),
            telephoneArchive: telephoneArchive || null,
        };
    }
}

export { TelephoneFormDataVM };
